import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import ComplaintCard from './ComplaintCard';
import { ChoiceButton } from '../guestReviews/shared';
import { useComplaintQueue } from '../../hooks/useComplaints';
import { formatCount } from '../../utils/numberFormatter';
import { LISTS, LIST_LABELS, LIST_EMPTY, TEXT } from '../../config/complaintLabels';
import '../../styles/ComplaintsPage.css';

// «بحاجة لإجراء» — the list the menu counter counts, so the page opens on it.
const DEFAULT_VIEW = LISTS[0];

/**
 * The «الشكاوى» tab of «التقييمات والشكاوى» (tasks/complaints-to-fixes.md C15, C19 — one page for ops, not two).
 *
 * Its lists are FILTER BUTTONS, not tabs — no tabs inside a tab: needs action · in progress · closed recently,
 * kept in the URL (`?view=`). ONE complaint is open at a time (`?id=`, so a link from the «فتح شكوى» form lands on
 * it), and it is PINNED above the lists rather than inside one: an action moves a complaint between lists (a
 * repair task → «قيد التنفيذ», closing → «أُغلقت مؤخراً»), and a card that moved would be rebuilt — losing
 * whatever the person had typed in its other sections.
 *
 * Mounted only while its tab is open, so the complaints list is read then and never before. Which list a
 * complaint sits in, what is late and what still blocks closing it are the server's — this only filters by the
 * server's `lists`. Nothing is counted before the server has answered.
 */
const ComplaintsTab = () => {
  const { queue, isLoading, isError, refetch } = useComplaintQueue();
  const { counts, items, defaults, options } = queue;
  const [searchParams, setSearchParams] = useSearchParams();
  const ready = !isLoading && !isError;

  const view = LISTS.includes(searchParams.get('view')) ? searchParams.get('view') : DEFAULT_VIEW;
  const openId = searchParams.get('id');
  const opened = openId ? items.find(i => String(i.id) === openId) : null;
  // A link to a complaint that is in no list — closed long ago, or gone.
  const notListed = ready && openId && !opened;

  // The page's own `list=complaints` always stays; only `view` and `id` change here.
  const go = ({ nextView, nextId }, replace = false) => setSearchParams({
    list: 'complaints',
    ...(nextView !== DEFAULT_VIEW ? { view: nextView } : {}),
    ...(nextId ? { id: nextId } : {})
  }, replace ? { replace: true } : undefined);
  const choose = (key) => go({ nextView: key, nextId: openId });
  const toggle = (id) => go({ nextView: view, nextId: String(id) === openId ? null : String(id) }, true);

  const inView = items.filter(i => i.lists.includes(view));

  return (
    <>
      {isLoading && (
        <div className="gr-state"><Spinner animation="border" size="sm" /> {TEXT.loading}</div>
      )}
      {isError && (
        <div className="gr-state gr-error" role="alert">
          {TEXT.loadError}{' '}
          <Button variant="link" className="gr-link" onClick={() => refetch()}>{TEXT.refresh}</Button>
        </div>
      )}
      {notListed && <div className="gr-notice">{TEXT.notListed}</div>}

      {opened && (
        <section className="cp-pinned" aria-label={TEXT.openNow}>
          <ComplaintCard item={opened} defaults={defaults} options={options} open onToggle={() => toggle(opened.id)} />
        </section>
      )}

      <div className="gr-choices cp-views" role="group" aria-label={TEXT.viewsLabel}>
        {LISTS.map(key => (
          <ChoiceButton key={key} active={view === key} onClick={() => choose(key)}>
            {ready ? `${LIST_LABELS[key]} (${formatCount(counts[key])})` : LIST_LABELS[key]}
          </ChoiceButton>
        ))}
      </div>

      {ready && inView.length === 0 && (
        <div className="gr-empty">
          <i className="bi bi-exclamation-diamond" aria-hidden="true"></i>
          <p>{LIST_EMPTY[view]}</p>
          {view === DEFAULT_VIEW && <p className="gr-muted">{TEXT.emptyHint}</p>}
        </div>
      )}
      {inView.filter(i => String(i.id) !== openId).map(item => (
        <ComplaintCard key={item.id} item={item} defaults={defaults} options={options} open={false} onToggle={() => toggle(item.id)} />
      ))}
    </>
  );
};

export default ComplaintsTab;
